import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { consumeMirrorSession } from '../services/authApi';
import { getAdminContext } from '../services/adminApi';
import { isPortalAdmin } from '../utils/portalRoles';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const storedToken = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');

  const [user, setUser] = useState(storedUser ? JSON.parse(storedUser) : null);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(storedToken);
  const [portalContext, setPortalContext] = useState({ brands: [], salons: [] });

  const refreshPortalContext = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    const storedUserData = localStorage.getItem('user');
    let parsed = null;
    try {
      parsed = storedUserData ? JSON.parse(storedUserData) : null;
    } catch {
      parsed = null;
    }
    if (!currentToken || !parsed || !isPortalAdmin(parsed.role)) {
      setPortalContext({ brands: [], salons: [] });
      return;
    }
    try {
      const res = await getAdminContext();
      const data = res.data || {};
      setPortalContext({
        brands: Array.isArray(data.brands) ? data.brands : [],
        salons: Array.isArray(data.salons) ? data.salons : []
      });
    } catch (e) {
      console.error('Failed to load admin context', e);
    }
  }, []);

  useEffect(() => {
    if (token) {
      const storedUserData = localStorage.getItem('user');
      if (!user && storedUserData) {
        try {
          setUser(JSON.parse(storedUserData));
        } catch (e) {
          console.error('Error parsing stored user:', e);
        }
      }
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    refreshPortalContext();
  }, [token, user?.role, refreshPortalContext]);

  const login = (nextToken, userData) => {
    localStorage.setItem('token', nextToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(nextToken);
    setUser(userData);
  };

  const logout = () => {
    try {
      const mirrorId = localStorage.getItem('mirrorId');
      if (mirrorId) {
        consumeMirrorSession(mirrorId).catch(() => {});
      }
    } catch (_e) {
      // ignore
    }
    setToken(null);
    setUser(null);
    setPortalContext({ brands: [], salons: [] });
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    portalContext,
    refreshPortalContext
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
